import React from 'react';
import { Button, message, Popover, Tooltip } from 'antd';
import {
    CaseListItem,
    ICaseStatus,
    IPaymentStatus,
    IRefundExecutionStatus,
    TextVisitServiceType,
} from 'types/textVisit';
import { getTimeLeft, getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
import dayjs from 'utils/dayjs';
import s from './s.module.less';
import { QuestionCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { FollowUpServiceTypeMap } from 'pages/dashboard/TextVisitPage/Dashboard/components/MyCaseCard';
import { EIntakeFormStatus } from 'types/calendar';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { getIntakeFormUrlByTextVisitId } from 'utils/common';

interface TextVisitCardProps {
    caseData: CaseListItem;
    onViewDetails: () => void;
    onDeclineRefilleRequest?: (item: CaseListItem) => void;
    onCancelRecurringRequest?: (item: CaseListItem) => void;
    onEditRequest?: (item: CaseListItem) => void;
    onChargePatient?: (item: CaseListItem) => void;
    smallSize?: boolean;
    clientId?: number;
    showChargeAction?: boolean;
    hideViewDetails?: boolean;
    onViewIntakeForm?: () => void;
    activeTab?: 'InProgress' | 'PastDue' | 'Upcoming' | 'Past';
    isGroupPractice?: boolean;
}

const TextVisitCard: React.FC<TextVisitCardProps> = ({
    caseData,
    onViewDetails,
    smallSize = false,
    clientId,
    onDeclineRefilleRequest,
    onCancelRecurringRequest,
    onEditRequest,
    onChargePatient,
    showChargeAction = false,
    hideViewDetails = false,
    onViewIntakeForm,
    activeTab,
    isGroupPractice,
}) => {
    const isClosed = caseData.status === ICaseStatus.CLOSED;
    const hasRefund = Boolean(caseData.refundExecutionStatus);
    const isFollowUpCase = caseData.serviceType !== TextVisitServiceType.INITIAL_VISIT;
    const refillTitle = getRefillServiceTitle({
        serviceType: caseData.serviceType,
        specialty: (caseData as any).specialty ?? null,
        assessmentName: caseData.assessmentName,
    });

    const serviceTypeTitle = isFollowUpCase
        ? refillTitle || FollowUpServiceTypeMap[caseData.serviceType as keyof typeof FollowUpServiceTypeMap]?.title
        : caseData.assessmentName;
    const [sendingIntakeReminder, setSendingIntakeReminder] = React.useState<boolean>(false);

    const showIntakeFormPending =
        caseData?.intakeFormStatus === EIntakeFormStatus.REQUIRED ||
        caseData?.intakeFormStatus === EIntakeFormStatus.IN_PROGRESS;

    const showViewIntakeForm = caseData?.intakeFormStatus === EIntakeFormStatus.COMPLETED;

    const isRefillRequest = serviceTypeTitle?.toLowerCase()?.includes('refill');
    const notStart = !caseData?.providerTreatmentSubmitted;
    const isHeldForPayment =
        isRefillRequest && caseData?.providerTreatmentSubmitted && caseData?.noteSendStatus === 'PENDING_PAYMENT';
    const showNotStartTag = isRefillRequest && notStart;
    const heldReasonText = (() => {
        switch (caseData?.noteHoldReason) {
            case 'NO_PAYMENT_METHOD':
                return 'No payment method';
            case 'BILLING_FAILED':
                return 'Payment failed';
            case 'INVOICE_UNPAID':
                return 'Invoice unpaid';
            default:
                return null;
        }
    })();
    const isRecurringSeries = Boolean(
        (caseData as any)?.recurrenceRule ||
        (caseData as any)?.repeatDesc ||
        (caseData as any)?.isRecurringSeries ||
        (caseData as any)?.recurringSeriesId ||
        caseData.recurrenceId,
    );

    const isNewRecurringSeries = Boolean(caseData.recurrenceId);

    //todo
    const displayChargeAction = showChargeAction && caseData.paymentStatus === IPaymentStatus.SUCCEEDED;

    const RefundTextMap = {
        [IRefundExecutionStatus.PENDING_REVIEW]: {
            text: 'Refund request under review',
            color: '#92400E',
            backgroundColor: '#FEF3C7',
        },
        [IRefundExecutionStatus.WAITING_REFUNDED]: {
            text: 'Refund processed',
            color: '#065F46',
            backgroundColor: '#D1FAE5',
        },
        [IRefundExecutionStatus.IN_PROGRESS]: {
            text: 'Refund processed',
            color: '#065F46',
            backgroundColor: '#D1FAE5',
        },
        [IRefundExecutionStatus.REFUNDED]: {
            text: 'Refund processed',
            color: '#065F46',
            backgroundColor: '#D1FAE5',
        },
        [IRefundExecutionStatus.DENIED]: {
            text: 'Refund denied',
            color: '#9D174D',
            backgroundColor: '#FCE7F3',
        },
        [IRefundExecutionStatus.FAILED]: {
            text: 'Refund processed',
            color: '#065F46',
            backgroundColor: '#D1FAE5',
        },
    };

    const refundContent = hasRefund ? (
        <div className={s.refundContent}>
            <div className="flex flex-col gap-1">
                {[
                    IRefundExecutionStatus.REFUNDED,
                    IRefundExecutionStatus.WAITING_REFUNDED,
                    IRefundExecutionStatus.IN_PROGRESS,
                    IRefundExecutionStatus.FAILED,
                ].includes(caseData.refundExecutionStatus) && (
                    <div>
                        Refunded:<span className="font-bold ml-1">${(caseData.refundAmount || 0).toFixed(2)}</span>
                    </div>
                )}
                <div>
                    Refund Reason:<span className="font-bold ml-1">{caseData.refundReason}</span>
                </div>
                {caseData.notesToProvider &&
                    [IRefundExecutionStatus.REFUNDED, IRefundExecutionStatus.DENIED].includes(
                        caseData.refundExecutionStatus,
                    ) && (
                        <div>
                            Notes from Klarity team:<span className="font-bold ml-1">{caseData.notesToProvider}</span>
                        </div>
                    )}
            </div>
            {caseData.refundExecutionStatus === IRefundExecutionStatus.PENDING_REVIEW && (
                <div className="text-xs italic mt-4">
                    Patient’s refund request is currently under review. It usually takes 3-5 days. You’ll receive an
                    update by email when the review is complete.
                </div>
            )}
            {[
                IRefundExecutionStatus.WAITING_REFUNDED,
                IRefundExecutionStatus.IN_PROGRESS,
                IRefundExecutionStatus.FAILED,
            ].includes(caseData.refundExecutionStatus) && (
                <div className="text-xs italic mt-4">
                    Refund is being processed. It may take a few business days to appear in patient’s account.
                </div>
            )}
        </div>
    ) : null;

    if (isNewRecurringSeries) {
        const isPast = activeTab === 'Past';
        const isPastDue = activeTab === 'PastDue';
        const isInProgress = activeTab === 'InProgress';

        const recurringTitle = caseData.specialty
            ? `${caseData.specialty}: Prescription refill`
            : serviceTypeTitle || 'Prescription refill';

        const formatRecurrenceType = (t?: string) => {
            if (!t) return '';
            return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
        };
        const recurrenceTypeText = formatRecurrenceType(caseData.recurrenceType);
        const occurrenceLine = [
            recurrenceTypeText,
            caseData.endOccurrences ? `${caseData.endOccurrences} occurrences in total` : '',
        ]
            .filter(Boolean)
            .join(' | ');

        const visitDate = isPast ? caseData.patientSubmitAt : caseData.startAt;
        // Backend stores UTC. With Z it's auto-handled, without Z dayjs would treat as local —
        // use dayjs.utc to force UTC parse, then .local() to convert for display.
        const visitDateStr = visitDate ? dayjs.utc(visitDate).local().format('dddd, MMMM D') : '';
        const visitLine = `Next refill ${caseData.occurrenceIndex ?? ''}/${caseData.endOccurrences ?? ''}: ${visitDateStr}`;

        return (
            <div className={`${s.textVisitCard} ${smallSize ? s.smallSize : ''}`}>
                <div className="flex flex-col gap-1">
                    <p className={s.recurringTitle}>{`🔁 ${recurringTitle}`}</p>
                    {occurrenceLine && (
                        <div className={s.recurringInfoRow}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                            >
                                <path
                                    d="M6 5.5V3M12 5.5V3M5.25 8.5H12.75M3.75 16H14.25C15.0784 16 15.75 15.3284 15.75 14.5V5.5C15.75 4.67157 15.0784 4 14.25 4H3.75C2.92157 4 2.25 4.67157 2.25 5.5V14.5C2.25 15.3284 2.92157 16 3.75 16Z"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span>{occurrenceLine}</span>
                        </div>
                    )}
                    <div className={s.recurringInfoRow}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M9 3.75V9L12 10.5M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span>{visitLine}</span>
                    </div>
                </div>
                <div className={s.actionWrap}>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                        {isGroupPractice &&
                            (caseData.assignedProviderFirstName || caseData.assignedProviderLastName) && (
                                <span className={s.fromProvider}>
                                    from{' '}
                                    {`${caseData.assignedProviderFirstName || ''} ${caseData.assignedProviderLastName || ''}`.trim()}
                                </span>
                            )}
                        {isPast && caseData.paymentStatus === IPaymentStatus.SUCCEEDED && (
                            <span className={s.tag} style={{ background: '#D1FAE5', color: '#065F46' }}>
                                Paid
                            </span>
                        )}
                        {isPast && (
                            <span className={s.tag} style={{ background: '#D1FAE5', color: '#065F46' }}>
                                Completed
                            </span>
                        )}
                        {isInProgress && (
                            <span className={s.tag} style={{ background: '#FEF3C7', color: '#92400E' }}>
                                In-progress
                            </span>
                        )}
                        {isPastDue && (
                            <span className={s.tag} style={{ background: '#FEE2E2', color: '#991B1B' }}>
                                Past due
                            </span>
                        )}
                    </div>
                    <div className={s.right}>
                        {isPastDue && (
                            <Button
                                onClick={() => onCancelRecurringRequest?.(caseData)}
                                style={{ height: '30px', marginRight: '8px' }}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button onClick={onViewDetails} style={{ height: '30px' }}>
                            {isInProgress ? 'Resume' : 'View'}
                        </Button>
                        {!isPast && !isInProgress && !isPastDue && (
                            <Popover
                                placement="bottomLeft"
                                title={null}
                                content={
                                    <div>
                                        {(caseData as any)?.canModifyRecurrence && (
                                            <div className={s.pop} onClick={() => onEditRequest?.(caseData)}>
                                                Edit
                                            </div>
                                        )}
                                        <div className={s.pop} onClick={() => onCancelRecurringRequest?.(caseData)}>
                                            Cancel
                                        </div>
                                    </div>
                                }
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    style={{ marginLeft: '8px', cursor: 'pointer' }}
                                >
                                    <path
                                        d="M11.9984 7.20039C10.673 7.20039 9.59844 6.12587 9.59844 4.80039C9.59844 3.47491 10.673 2.40039 11.9984 2.40039C13.3239 2.40039 14.3984 3.47491 14.3984 4.80039C14.3984 6.12588 13.3239 7.20039 11.9984 7.20039Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M11.9984 14.4004C10.673 14.4004 9.59844 13.3259 9.59844 12.0004C9.59844 10.6749 10.673 9.60039 11.9984 9.60039C13.3239 9.60039 14.3984 10.6749 14.3984 12.0004C14.3984 13.3259 13.3239 14.4004 11.9984 14.4004Z"
                                        fill="#6B7280"
                                    />
                                    <path
                                        d="M9.59844 19.2004C9.59844 20.5259 10.673 21.6004 11.9984 21.6004C13.3239 21.6004 14.3984 20.5259 14.3984 19.2004C14.3984 17.8749 13.3239 16.8004 11.9984 16.8004C10.673 16.8004 9.59844 17.8749 9.59844 19.2004Z"
                                        fill="#6B7280"
                                    />
                                </svg>
                            </Popover>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${s.textVisitCard} ${smallSize ? s.smallSize : ''}`}>
            <div className="flex flex-col gap-1">
                <div className={`font-medium ${s.titleRow}`}>
                    {isRecurringSeries && (
                        <span className={s.recurringIcon} title="Recurring series">
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.333 10a6.667 6.667 0 0 1 11.667-4.167"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M15 3.333v2.5H12.5"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M16.667 10a6.667 6.667 0 0 1-11.667 4.167"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M5 16.667v-2.5h2.5"
                                    stroke="#6B7280"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    )}
                    <span>{serviceTypeTitle}: Text visit</span>
                    {isGroupPractice && (caseData.assignedProviderFirstName || caseData.assignedProviderLastName) && (
                        <span className={s.fromProvider}>
                            from{' '}
                            {`${caseData.assignedProviderFirstName || ''} ${caseData.assignedProviderLastName || ''}`.trim()}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                        <path
                            d="M6.5 6.17432V3.17432M12.5 6.17432V3.17432M5.75 9.17432H13.25M4.25 16.6743H14.75C15.5784 16.6743 16.25 16.0027 16.25 15.1743V6.17432C16.25 5.34589 15.5784 4.67432 14.75 4.67432H4.25C3.42157 4.67432 2.75 5.34589 2.75 6.17432V15.1743C2.75 16.0027 3.42157 16.6743 4.25 16.6743Z"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    {isRefillRequest && caseData.startAt && (
                        <>
                            <span>
                                Refill on{' '}
                                {dayjs
                                    .utc(caseData.startAt)
                                    .local()
                                    .format(isClosed ? 'MMM D, YYYY' : 'MMM D')}
                            </span>
                            |
                        </>
                    )}
                    <span>
                        Submitted on {dayjs(caseData.patientSubmitAt).format(isClosed ? 'MMM D, YYYY' : 'MMM D')}
                    </span>
                    |<span>Last updated on {dayjs(caseData.updatedAt).format(isClosed ? 'MMM D, YYYY' : 'MMM D')}</span>
                </div>
                {/* {showIntakeFormPending && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M6 3.75H4.5C3.67157 3.75 3 4.42157 3 5.25V14.25C3 15.0784 3.67157 15.75 4.5 15.75H12C12.8284 15.75 13.5 15.0784 13.5 14.25V13.5M6 3.75C6 4.57843 6.67157 5.25 7.5 5.25H9C9.82843 5.25 10.5 4.57843 10.5 3.75M6 3.75C6 2.92157 6.67157 2.25 7.5 2.25H9C9.82843 2.25 10.5 2.92157 10.5 3.75M10.5 3.75H12C12.8284 3.75 13.5 4.42157 13.5 5.25V7.5M15 10.5H7.5M7.5 10.5L9.75 8.25M7.5 10.5L9.75 12.75"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className={s.intakeFormItem}>
                            <div className={s.intakeFormItemLabel}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.25803 3.09956C9.02264 1.74025 10.9797 1.74025 11.7443 3.09956L17.3246 13.0201C18.0746 14.3533 17.1111 16.0006 15.5815 16.0006H4.42088C2.89123 16.0006 1.9278 14.3533 2.67773 13.0201L8.25803 3.09956ZM11.0011 13.0007C11.0011 13.553 10.5534 14.0007 10.0011 14.0007C9.44881 14.0007 9.0011 13.553 9.0011 13.0007C9.0011 12.4484 9.44881 12.0007 10.0011 12.0007C10.5534 12.0007 11.0011 12.4484 11.0011 13.0007ZM10.0011 5.00073C9.44881 5.00073 9.0011 5.44845 9.0011 6.00073V9.00073C9.0011 9.55302 9.44881 10.0007 10.0011 10.0007C10.5534 10.0007 11.0011 9.55302 11.0011 9.00073V6.00073C11.0011 5.44845 10.5534 5.00073 10.0011 5.00073Z"
                                        fill="#D97706"
                                    />
                                </svg>
                                Intake form pending
                            </div>
                            <div className={s.intakeFormItemSend}>
                                <Button
                                    className={s.intakeFormReminderBtn}
                                    loading={sendingIntakeReminder}
                                    onClick={handleSendIntakeFormReminder}
                                    type="text"
                                >
                                    <span>Send reminder</span>
                                    <Tooltip
                                        overlayClassName={s.tipCard}
                                        overlayInnerStyle={{
                                            width: '330px',
                                            padding: '16px',
                                            borderRadius: '24px',
                                        }}
                                        color="#ffffff"
                                        title={
                                            <div className={s.tooltipContent} style={{ color: '#333' }}>
                                                <div className={s.tooltipTitle}>
                                                    📩 Intake form reminder message preview
                                                </div>
                                                <div className={s.tooltipMessage}>
                                                    <p>Hi [Patient&apos;s Name],</p>
                                                    <p>
                                                        Just a quick reminder to complete your intake form to help us
                                                        prepare for your visit. Please fill it out at your earliest
                                                        convenience using the link below:
                                                    </p>
                                                    <p className={s.tooltipLink}>[Intake Form URL]</p>
                                                    <p>
                                                        If you have any questions, feel free to reach out. We&apos;re
                                                        here to help!
                                                    </p>
                                                </div>
                                            </div>
                                        }
                                        placement="top"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            className={s.questionIcon}
                                        >
                                            <path
                                                d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                                                stroke="#6B7280"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Tooltip>
                                </Button>
                            </div>
                        </div>
                    </div>
                )} */}
                {showViewIntakeForm && (
                    <div className={s.vItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M6 3.75H4.5C3.67157 3.75 3 4.42157 3 5.25V14.25C3 15.0784 3.67157 15.75 4.5 15.75H12C12.8284 15.75 13.5 15.0784 13.5 14.25V13.5M6 3.75C6 4.57843 6.67157 5.25 7.5 5.25H9C9.82843 5.25 10.5 4.57843 10.5 3.75M6 3.75C6 2.92157 6.67157 2.25 7.5 2.25H9C9.82843 2.25 10.5 2.92157 10.5 3.75M10.5 3.75H12C12.8284 3.75 13.5 4.42157 13.5 5.25V7.5M15 10.5H7.5M7.5 10.5L9.75 8.25M7.5 10.5L9.75 12.75"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className={s.viewLink} onClick={onViewIntakeForm}>
                            View intake forms
                        </div>
                    </div>
                )}
            </div>
            <div className={s.actionWrap}>
                <div className={`flex items-center gap-1 ${smallSize ? 'flex-col flex-start' : ''}`}>
                    {hasRefund && (
                        <Popover content={refundContent}>
                            <div
                                className={`${s.refundTag} ${s.tag}`}
                                style={{
                                    backgroundColor: RefundTextMap[caseData.refundExecutionStatus].backgroundColor,
                                    color: RefundTextMap[caseData.refundExecutionStatus].color,
                                }}
                            >
                                {RefundTextMap[caseData.refundExecutionStatus].text}
                                <QuestionCircleOutlined color={RefundTextMap[caseData.refundExecutionStatus].color} />
                            </div>
                        </Popover>
                    )}
                    {isHeldForPayment ? (
                        <Tooltip
                            title={
                                heldReasonText
                                    ? `Visit notes on hold — awaiting patient payment. Reason: ${heldReasonText}.`
                                    : 'Visit notes on hold — awaiting patient payment.'
                            }
                        >
                            <div className={`${s.tag} ${s.heldTag}`}>
                                <ClockCircleOutlined />
                                Payment on hold
                            </div>
                        </Tooltip>
                    ) : (
                        showNotStartTag && <div className={`${s.tag} ${s.notStartTag}`}>Not started</div>
                    )}
                    {!isRefillRequest && (
                        <div>
                            {!isClosed ? (
                                <div className={`${s.tag} ${s.activeTag}`}>
                                    Active: {getTimeLeft(caseData.assignedProviderAt!).text}
                                </div>
                            ) : (
                                <div className={`${s.tag} ${s.closedTag}`}>Closed</div>
                            )}
                        </div>
                    )}
                </div>
                <div className={s.right}>
                    {!hideViewDetails && (
                        <Button onClick={onViewDetails} style={{ height: '30px' }}>
                            View details
                        </Button>
                    )}

                    {displayChargeAction && (
                        <Button
                            onClick={() => {
                                onChargePatient?.(caseData);
                            }}
                            style={{ height: '30px', marginLeft: '8px' }}
                        >
                            Charge patient
                        </Button>
                    )}
                    {showNotStartTag && (
                        <Popover
                            placement="bottomLeft"
                            title={null}
                            content={
                                <div>
                                    {isRecurringSeries && (caseData as any)?.canModifyRecurrence && (
                                        <div className={s.pop} onClick={() => onEditRequest?.(caseData)}>
                                            Edit
                                        </div>
                                    )}
                                    {!isRecurringSeries &&
                                        caseData.startAt &&
                                        dayjs.utc(caseData.startAt).isAfter(dayjs.utc()) && (
                                            <div className={s.pop} onClick={() => onEditRequest?.(caseData)}>
                                                Edit
                                            </div>
                                        )}
                                    <div
                                        className={s.pop}
                                        onClick={() => {
                                            if (isRecurringSeries) {
                                                onCancelRecurringRequest?.(caseData);
                                            } else {
                                                onDeclineRefilleRequest?.(caseData);
                                            }
                                        }}
                                    >
                                        {isRecurringSeries ? 'Cancel' : 'Decline refill request'}
                                    </div>
                                </div>
                            }
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M11.9984 7.20039C10.673 7.20039 9.59844 6.12587 9.59844 4.80039C9.59844 3.47491 10.673 2.40039 11.9984 2.40039C13.3239 2.40039 14.3984 3.47491 14.3984 4.80039C14.3984 6.12588 13.3239 7.20039 11.9984 7.20039Z"
                                    fill="#6B7280"
                                />
                                <path
                                    d="M11.9984 14.4004C10.673 14.4004 9.59844 13.3259 9.59844 12.0004C9.59844 10.6749 10.673 9.60039 11.9984 9.60039C13.3239 9.60039 14.3984 10.6749 14.3984 12.0004C14.3984 13.3259 13.3239 14.4004 11.9984 14.4004Z"
                                    fill="#6B7280"
                                />
                                <path
                                    d="M9.59844 19.2004C9.59844 20.5259 10.673 21.6004 11.9984 21.6004C13.3239 21.6004 14.3984 20.5259 14.3984 19.2004C14.3984 17.8749 13.3239 16.8004 11.9984 16.8004C10.673 16.8004 9.59844 17.8749 9.59844 19.2004Z"
                                    fill="#6B7280"
                                />
                            </svg>
                        </Popover>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TextVisitCard;
